// MACHINERY V2 (Stroje) — status glyph set (M2.1).
//
// The icon half of the colour-blind-safety rule (§4.3): every connected
// state renders a colour AND a shape. Extracted into its own module because
// four separate contract components consume the same glyphs (StatusBadge,
// AlarmStrip, ProgramLine, StateCard's no-telemetry footer) — a shared set,
// not a premature abstraction.
//
// Verbatim port of StatusIcon from design_handoff_machinery_v3/
// machinery-live.jsx (12x12 viewBox, currentColor, aria-hidden).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { MachineryV2IconKind } from "./cardStates";

export interface StatusIconProps {
  kind: MachineryV2IconKind;
  size?: number;
}

const StatusIcon: React.FC<StatusIconProps> = ({ kind, size = 12 }) => {
  const s: React.CSSProperties = {
    width: size,
    height: size,
    display: "inline-block",
    flexShrink: 0,
  };

  switch (kind) {
    case "play":
      return (
        <svg viewBox="0 0 12 12" style={s} aria-hidden>
          <path d="M3 1.6 L9.5 6 L3 10.4 Z" fill="currentColor" />
        </svg>
      );
    case "pause":
      return (
        <svg viewBox="0 0 12 12" style={s} aria-hidden>
          <rect x="3" y="2" width="2.2" height="8" fill="currentColor" />
          <rect x="6.8" y="2" width="2.2" height="8" fill="currentColor" />
        </svg>
      );
    case "square":
      return (
        <svg viewBox="0 0 12 12" style={s} aria-hidden>
          <rect x="2.5" y="2.5" width="7" height="7" rx="0.5" fill="currentColor" />
        </svg>
      );
    case "alert":
      return (
        <svg viewBox="0 0 12 12" style={s} aria-hidden>
          <path d="M6 1.4 L11 10.5 L1 10.5 Z" fill="currentColor" />
          {/* The exclamation is knocked out in the card fill, not white, so the
              glyph stays legible on both palettes. */}
          <rect x="5.4" y="4.6" width="1.2" height="3" fill="var(--card)" />
          <rect x="5.4" y="8.2" width="1.2" height="1.2" fill="var(--card)" />
        </svg>
      );
    case "cloudoff":
      return (
        <svg
          viewBox="0 0 12 12"
          style={s}
          aria-hidden
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
        >
          <path d="M2.5 7.5 c0 -1.5 1.2 -2.7 2.7 -2.7 c0.4 -1.1 1.5 -1.9 2.7 -1.9 c1.6 0 2.9 1.3 2.9 2.9 c0.6 0.2 1.1 0.8 1.1 1.5 c0 0.9 -0.7 1.6 -1.6 1.6 H4.5" />
          <line x1="1.4" y1="1.4" x2="10.6" y2="10.6" strokeLinecap="round" />
        </svg>
      );
    case "hourglass":
      return (
        <svg
          viewBox="0 0 12 12"
          style={s}
          aria-hidden
          fill="none"
          stroke="currentColor"
          strokeWidth="1.1"
        >
          <path d="M3 2 H9 L6.2 6 L9 10 H3 L5.8 6 Z" />
        </svg>
      );
    default:
      return null;
  }
};

export default StatusIcon;
